import {
  BrandAssetKind,
  BrandAssetLibraryItem,
  BrandAssetNameMaxLength,
} from "@arrowsup/platform-dtos";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FontDownloadIcon from "@mui/icons-material/FontDownload";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { describeUsage } from "../../logic/brand-kit/describe-usage";
import {
  useBrandAssetLibrary,
  useBrandAssetMutations,
} from "../../logic/brand-kit/use-brand-kit";
import { BrandAssetUploadButton } from "./brand-assets";

const describeFile = (asset: BrandAssetLibraryItem): string => {
  const format = asset.mimeType.split("/")[1].replace("+xml", "");
  const size =
    asset.byteSize < 1024 * 1024
      ? `${Math.max(1, Math.round(asset.byteSize / 1024))} KB`
      : `${(asset.byteSize / 1024 / 1024).toFixed(1)} MB`;
  const dimensions =
    asset.width && asset.height ? `${asset.width}×${asset.height}` : undefined;
  const parts = [format.toUpperCase(), dimensions, size];
  return parts.filter((part) => part).join(" · ");
};

const RenameDialog: React.FC<{
  asset: BrandAssetLibraryItem;
  onClose: () => void;
}> = ({ asset, onClose }) => {
  const [name, setName] = useState(asset.name);
  const { update } = useBrandAssetMutations();
  const trimmed = name.trim();

  const save = () =>
    update.mutate({ id: asset.id, name: trimmed }, { onSuccess: onClose });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Rename</DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: BrandAssetNameMaxLength }}
          autoFocus
          fullWidth
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!trimmed || update.isPending}
          onClick={save}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AssetCard: React.FC<{ asset: BrandAssetLibraryItem }> = ({ asset }) => {
  const [renaming, setRenaming] = useState(false);
  const { update, remove } = useBrandAssetMutations();
  const usage = describeUsage(asset.usage);

  const onDelete = () => {
    if (window.confirm(`Delete "${asset.name}"? This can't be undone.`)) {
      remove.mutate(asset.id);
    }
  };

  return (
    <Card variant="outlined" component="article" aria-label={asset.name}>
      <Box
        sx={{
          height: 140,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
        }}
      >
        {asset.kind === BrandAssetKind.Image ? (
          <Box
            component="img"
            src={asset.previewUrl ?? asset.url}
            alt={asset.name}
            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        ) : (
          <FontDownloadIcon fontSize="large" color="action" />
        )}
      </Box>
      <CardContent>
        <Typography variant="subtitle2" noWrap title={asset.name}>
          {asset.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p">
          {describeFile(asset)}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p">
          {usage ?? "Not used"}
        </Typography>
        {asset.archived && (
          <Chip size="small" label="Archived" sx={{ mt: 1 }} />
        )}
      </CardContent>
      <CardActions>
        <Tooltip title="Rename">
          <IconButton size="small" onClick={() => setRenaming(true)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={asset.archived ? "Unarchive" : "Archive"}>
          <IconButton
            size="small"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({ id: asset.id, archived: !asset.archived })
            }
          >
            {asset.archived ? (
              <UnarchiveIcon fontSize="small" />
            ) : (
              <ArchiveIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title={usage ? `${usage}, so it can't be deleted` : "Delete"}>
          <span>
            <IconButton
              size="small"
              aria-label="Delete"
              disabled={!!usage || remove.isPending}
              onClick={onDelete}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </CardActions>
      {renaming && (
        <RenameDialog asset={asset} onClose={() => setRenaming(false)} />
      )}
    </Card>
  );
};

/** The org's images and fonts, for use in image and font fields. */
export const BrandAssetLibraryView: React.FC = () => {
  const { data: library, isLoading, isError } = useBrandAssetLibrary();
  const [showArchived, setShowArchived] = useState(false);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !library) {
    return <Alert severity="error">Failed to load the asset library</Alert>;
  }

  const shown = library.filter((asset) => showArchived || !asset.archived);
  const archivedCount = library.filter((asset) => asset.archived).length;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" color="text.secondary" flex={1}>
          Logos, example ads and other images, and font files. Use them in image
          and font fields. Archiving hides an asset from the pickers; versions
          already using it keep it.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
          }
          label={`Show archived (${archivedCount})`}
        />
        <BrandAssetUploadButton />
      </Stack>
      {shown.length === 0 ? (
        <Typography color="text.secondary">
          No assets yet. Upload PNG, JPEG, GIF, WebP or SVG images and WOFF2,
          WOFF, TTF or OTF fonts, up to 5MB each.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 2,
          }}
        >
          {shown.map((asset) => (
            <AssetCard key={asset.id} asset={asset} />
          ))}
        </Box>
      )}
    </Stack>
  );
};
